// Sovelluksen runko: tila, palvelinyhteys, tallennin ja näkymien vaihto.
//
// Kaikki näkymät saavat yhden `app`-olion, jossa on sekä data että toiminnot.
// Näin tilanhallinta pysyy yhdessä tiedostossa ja näkymät ovat pelkkää esitystä.

import "@fontsource/atkinson-hyperlegible/400.css";
import "@fontsource/atkinson-hyperlegible/700.css";
import "@fontsource/barlow-condensed/500.css";
import "@fontsource/barlow-condensed/600.css";
import "@fontsource/fraunces/latin-600.css";
import "@fontsource/fraunces/latin-700.css";
import "@fontsource/newsreader/latin-400.css";
import "@fontsource/newsreader/latin-600.css";
import "@fontsource/lilita-one/latin-400.css";
import "@fontsource/rubik/latin-500.css";
import "@fontsource/rubik/latin-700.css";
import "@fontsource/outfit/latin-400.css";
import "@fontsource/outfit/latin-600.css";
import "@fontsource/vt323/latin-400.css";
import "./styles.css";
import "./styles-skins.css";

import { render } from "preact";
import { AccountGate } from "./components/AccountViews.jsx";
import { LeaderboardPage } from "./components/LeaderboardPage.jsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";

import { ContentSheet, ContentView } from "./components/ContentView.jsx";
import { HiddenFormSheet } from "./components/HiddenAdmin.jsx";
import { DiaryView } from "./components/DiaryView.jsx";
import { BaseView } from "./components/BaseView.jsx";
import { IconLeaderboard, IconControl, IconDiary, IconMap, IconPassport } from "./components/icons.jsx";
import { MapScreen } from "./components/MapScreen.jsx";
import { PassView } from "./components/PassView.jsx";
import { api } from "./lib/api.js";
import { UpdateSheet } from "./components/UpdateSheet.jsx";
import { WelcomeSheet } from "./components/PlayerViews.jsx";
import { useAppUpdate } from "./lib/useAppUpdate.js";
import { AdventureOutbox } from "./lib/adventureOutbox.js";
import { completionPosts, isFinished, punch, routeBounds, startAdventure } from "./lib/adventure.js";
import { lineBounds } from "./lib/routeLayer.js";
import { poiBounds } from "./lib/poiLayer.js";
import { backStack, backStep } from "./lib/backStack.js";
import { effectiveBasemap } from "./lib/basemap.js";
import { isEligible, modeLabel } from "./lib/modes.js";
import { km, points as fmtPoints } from "./lib/format.js";
import { defaultLocationSource, simulatorSource } from "./lib/location.js";
import { shareErrorText, shareImport } from "./lib/shareImport.js";
import { shapesWanted } from "./lib/shapeLayers.js";
import { markWinnersSeen, pendingWinnerNotices } from "./lib/leaderboard.js";
import { DEFAULT_SKIN, normalizeSkin, skinById } from "./lib/skins.js";
import { cleanCompanions, earnedLines, findPlayer, OWNER } from "./lib/players.js";
import { Recorder } from "./lib/recorder.js";
import { storage } from "./lib/storage.js";
import { createPointJournal } from "./lib/pointJournal.js";
import { recoverRecorderSnapshot } from "./lib/recorderMirror.js";
import { logEvent } from "./lib/diagnostics.js";
import { hintToast, pollDue, unannounced } from "./lib/hidden.js";
import { needsRecenter } from "./lib/mapStart.js";
import { loadSnapshot, saveSnapshot } from "./lib/snapshotCache.js";
import { applyPrefs, vibrate } from "./lib/prefs.js";

applyPrefs();

// Leimaus vaatii tarkan sijainnin: tätä epätarkempi paikannus ei leimaa rasteja.
const MAX_PUNCH_ACCURACY_M = 60;

// Seikkailupalkin ja aloituspainikkeen viemä korkeus kartan alareunasta (kuvapisteinä).
const ADVENTURE_DOCK_PX = 260;
const ROUTE_DOCK_PX = 230; // tila alareunan reittipalkille + aloituspainikkeelle, kun reitti tarkennetaan kartalle

const DEV = new URLSearchParams(location.search).has("dev");

// Käynnistyksessä luettu pistevarasto (täytetään alla ennen renderöintiä).
const startup = { journal: null, journalPoints: new Map() };
const TABS = [
  { id: "map", label: "Kartta", Icon: IconMap },
  { id: "content", label: "Kohteet", Icon: IconControl },
  { id: "pass", label: "Passi", Icon: IconPassport },
  { id: "diary", label: "Päiväkirja", Icon: IconDiary },
  { id: "leaderboard", label: "Sijoitukset", Icon: IconLeaderboard },
];

// --- Palkintoilmoitukset -------------------------------------------------------

/** Yhdistää saman lajin ruutupalkinnot yhdeksi ilmoitukseksi, jotta ruutu ei täyty. */
function summarizeChanges(changes) {
  const toasts = [];
  const grouped = new Map();
  for (const c of changes) {
    if (c.kind === "tile" || c.kind === "bigtile") {
      const g = grouped.get(`${c.kind}:${c.reason}`) ?? { ...c, count: 0, delta: 0 };
      g.count += 1;
      g.delta += c.delta;
      grouped.set(`${c.kind}:${c.reason}`, g);
    } else {
      toasts.push({ kind: c.reason === "reversal" ? "reversal" : c.kind, delta: c.delta, what: c.title, sub: c.label });
    }
  }
  for (const g of grouped.values()) {
    const big = g.kind === "bigtile";
    const what =
      g.reason === "award"
        ? g.count === 1 ? (big ? "Uusi suuri ruutu" : "Uusi pikkuruutu") : `${g.count} uutta ${big ? "suurta ruutua" : "pikkuruutua"}`
        : `${g.count} ${big ? (g.count === 1 ? "suuri ruutu" : "suurta ruutua") : g.count === 1 ? "pikkuruutu" : "pikkuruutua"}`;
    const sub = g.reason === "reversal" ? "Oikaisu" : g.reason === "restore" ? "Palautettu" : null;
    toasts.unshift({ kind: g.reason === "reversal" ? "reversal" : g.kind, delta: g.delta, what, sub });
  }
  return toasts;
}

function Toasts({ items }) {
  return (
    <div class="toasts" aria-live="polite">
      {items.map((t) => (
        <div class={`toast ${t.kind}`} key={t.id}>
          {t.delta != null && <span class="delta">{fmtPoints(t.delta)}</span>}
          <div>
            <div class="what">{t.what}</div>
            {t.sub && <div class="sub">{t.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Sovellus ----------------------------------------------------------------

function App({ auth }) {
  const [tab, setTab] = useState("map");
  // Viimeksi haettu pelitila näytetään heti, myös ilman palvelinta (lib/snapshotCache.js);
  // seuraava onnistunut haku korvaa sen.
  const startPlayer = auth.player.id;
  const [stats, setStats] = useState(() => loadSnapshot(storage, startPlayer, "stats"));
  const [tiles, setTiles] = useState(() => loadSnapshot(storage, startPlayer, "tiles"));
  const [content, setContent] = useState(() => loadSnapshot(storage, startPlayer, "content"));
  const [trips, setTrips] = useState([]);
  // Päiväkirjan tila: "loading" (ensimmäinen haku kesken), "ready" tai "error" (haku epäonnistui). Tyhjä lista ei saa näyttää "ei retkiä", ennen kuin haku on onnistunut.
  const [tripsStatus, setTripsStatus] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [dataVersion, setDataVersion] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [me, setMeRaw] = useState(null);
  // Aikaleima mukaan: varasijainnin ikä tarvitaan, kun tuoretta GPS-hakua ei saada (lib/location.js fixNow).
  const setMe = (loc) => setMeRaw(loc ? { ...loc, at: Date.now() } : loc);
  // Pyyntö keskittää kartta pelaajaan (id kasvaa joka kerta; force ohittaa "käyttäjä siirsi karttaa" -tarkistuksen).
  const [centerRequest, setCenterRequest] = useState(null);
  const [locationError, setLocationError] = useState(null);
  // Salaiset kohteet: lähellä olevien vihjeet (ei paikkaa) ja ylläpitäjän lomake. Ylläpitoavain on puhelimessa;
  // ilman sitä kehittäjätoiminnot (loki, kohteiden hallinta) eivät näy.
  const [hints, setHints] = useState([]);
  const [adminKey, setAdminKeyState] = useState(() => storage.get("adminKey", ""));
  const [hiddenForm, setHiddenForm] = useState(null);
  const [openItem, setOpenItem] = useState(null); // { kind, id }
  const [openTripId, setOpenTripId] = useState(null);
  const [sharedFile, setSharedFile] = useState(null); // Jaa-valikosta tullut tiedosto: { name, b64 } (lib/shareImport.js)
  const [theme, setThemeState] = useState(storage.get("theme", "auto"));
  const [skin, setSkinState] = useState(() => normalizeSkin(storage.get("skin", DEFAULT_SKIN)));
  const [wishlists, setWishlists] = useState([]);
  const [weekly, setWeekly] = useState(null);
  // Palvelimen karttatasot (MML). Viimeksi tiedetty lista säilytetään puhelimessa: ilman sitä
  // offline-käynnistyksessä kartta vaihtuisi OpenStreetMapiin, vaikka ladatut MML-ruudut ovat puhelimessa.
  const [mapLayers, setMapLayers] = useState(() => {
    const saved = storage.get("mapLayers", null);
    return Array.isArray(saved) ? saved : null;
  });
  const [basemapChoice, setBasemapChoiceState] = useState(storage.get("basemap", "maastokartta"));
  // Kartan työkalu: { type: 'poi' | 'route' | 'tile' | 'impossible' | 'time', ... } tai null (ks. MapTools.jsx)
  const [tool, setTool] = useState(null);

  // Pelaajat (perhe): jokaisella oma kartta, pisteet ja tukikohta. Valittu pelaaja
  // säilyy sovelluksen sulkemisen yli ja menee jokaiseen palvelinkutsuun (lib/api.js).
  const [players, setPlayers] = useState(null);
  const [activePlayer, setActivePlayerState] = useState(() => auth.player.id);
  const [companions, setCompanionsState] = useState([]);
  // Valittu seikkailukortti ja sen edistyminen. Säilyy sovelluksen sulkemisen yli, koska
  // retki voi kestää pidempään kuin sovellus pysyy auki. Kortti kuuluu aina yhdelle pelaajalle.
  const [adventure, setAdventureState] = useState(() => {
    const saved = storage.get("adventure", null);
    return saved && saved.player === (storage.get("player", OWNER) ?? OWNER) ? saved : null;
  });
  const [adventureDone, setAdventureDone] = useState(null); // valmistumisilmoitus
  const [adventureLog, setAdventureLog] = useState([]);
  const playerRef = useRef(activePlayer);
  const playersRef = useRef(null);
  playersRef.current = players;

  // Kartan suodatus ja lisäkerrokset. Ne ovat käyttäjän valintoja, jotka säilyvät
  // sovelluksen sulkemisen yli.
  const [mapModes, setMapModesState] = useState(() => storage.get("mapModes", []));
  const [overlays, setOverlaysState] = useState(() => ({
    pois: true, // valmiit kohteet
    routes: true, // valmiit reittilinjat (näkyvät oletuksena, piilotettavissa)
    shapes: false, // maksimiklusteri ja -neliö
    next: false, // seuraavan neliön puuttuvat ruudut
    shapesLarge: false, // suurten ruutujen klusteri, neliö ja tavoite
    heat: false, // lämpökartta
    impossible: true, // mahdottomiksi merkityt ruudut
    ...storage.get("overlays", {}),
  }));
  const [filteredTiles, setFilteredTiles] = useState(null); // tyhjä = ei suodatusta
  const [shapes, setShapes] = useState(null); // maksimineliö ja klusteri (/api/tiles/shapes)
  const [mapPoi, setMapPoi] = useState(null); // Kohteet-listasta kartalle valittu kohde (tunniste)
  const [mapRoute, setMapRoute] = useState(null); // Kohteet-listasta kartalle valittu reitti (tunniste)
  const [focus, setFocus] = useState(null); // karttaan siirtyminen toisesta näkymästä: { bounds }
  const modesRef = useRef(mapModes);
  modesRef.current = mapModes;

  // --- Teema ----------------------------------------------------------------
  const setTheme = (value) => {
    storage.set("theme", value);
    setThemeState(value);
  };
  useEffect(() => {
    if (theme === "auto") delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
  }, [theme]);

  // Ulkoasu: oletus ei aseta attribuuttia (styles.css toimii sellaisenaan), muut asettavat data-skin.
  const setSkin = (value) => {
    const next = normalizeSkin(value);
    storage.set("skin", next);
    // Heti, ei vasta tehosteessa: kartta lukee värit CSS-muuttujista omassa tehosteessaan.
    if (next === DEFAULT_SKIN) delete document.documentElement.dataset.skin;
    else document.documentElement.dataset.skin = next;
    setSkinState(next);
  };
  useEffect(() => {
    const root = document.documentElement;
    if (skin === DEFAULT_SKIN) delete root.dataset.skin;
    else root.dataset.skin = skin;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", skinById(skin).chrome);
  }, [skin]);

  // --- Ilmoitukset ------------------------------------------------------------
  const toastSeq = useRef(0);
  const toast = useCallback((t) => {
    const id = ++toastSeq.current;
    setToasts((list) => [...list.slice(-2), { ...t, id }]);
    setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), 3800);
  }, []);

  // --- Datan haku -------------------------------------------------------------
  const refresh = useCallback(async () => {
    const who = playerRef.current; // pelaajan vaihto kesken haun: vanhan pelaajan vastaukset hylätään
    // Haut ovat toisistaan riippumattomia: yhden epäonnistuminen (esim. vanhempi
    // palvelinversio ilman toivelistoja) ei saa tyhjentää muita näkymiä.
    const loads = [
      [api.stats, (r) => {
        setStats(r);
        saveSnapshot(storage, who, "stats", r);
      }],
      [() => api.tiles(), (r) => {
        setTiles(r);
        saveSnapshot(storage, who, "tiles", r);
      }],
      // Suodatettu näkymä haetaan erikseen: päättely (esim. "onko ruutu jo kerätty")
      // käyttää aina suodattamatonta dataa.
      [() => (modesRef.current.length ? api.tiles(null, modesRef.current) : Promise.resolve(null)), setFilteredTiles],
      [api.content, (r) => {
        setContent(r);
        saveSnapshot(storage, who, "content", r);
      }],
      [api.trips, setTrips],
      [api.wishlists, setWishlists],
      [api.weekly, setWeekly],
      [api.players, (r) => setPlayers(r.players)],
      [api.adventureLog, setAdventureLog],
      [api.mapLayers, (r) => {
        setMapLayers(r.layers);
        storage.set("mapLayers", r.layers);
      }],
    ];
    const results = await Promise.allSettled(loads.map(([load]) => load()));
    if (playerRef.current !== who) return;
    results.forEach((r, i) => r.status === "fulfilled" && loads[i][1](r.value));
    const tripsResult = results[loads.findIndex(([load]) => load === api.trips)];
    setTripsStatus(tripsResult.status === "fulfilled" ? "ready" : "error");
    // Jos karttatasoja ei saatu eikä aiempaa tietoa ole, pohjaksi OpenStreetMap
    // (tyhjä lista = palvelimella ei MML-tasoja).
    if (results.at(-1).status === "rejected") setMapLayers((current) => current ?? []);
    const failed = results.find((r) => r.status === "rejected");
    setLoadError(failed ? failed.reason.message : null);
    setDataVersion((v) => v + 1);
  }, []);

  // Suodatuksen vaihtuessa haetaan ruudut uudelleen.
  const modesKey = mapModes.join(",");
  const firstModes = useRef(true);
  useEffect(() => {
    if (firstModes.current) {
      firstModes.current = false;
      return;
    }
    refresh();
  }, [modesKey]);

  // Palkintojen jälkeen päivitetään näkymät pienellä viiveellä, jotta nopeasti
  // peräkkäiset erät eivät aiheuta turhia hakuja.
  const refreshTimer = useRef(null);
  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(refresh, 800);
  }, [refresh]);

  const showChanges = useCallback((changes) => {
    if (!changes?.length) return;
    summarizeChanges(changes).forEach(toast);
    if (changes.some((c) => c.delta > 0)) vibrate(40);
    const found = changes.filter((c) => c.kind === "secret" && c.delta > 0).map((c) => c.key.slice("secret:".length));
    if (found.length) setHints((list) => list.filter((h) => !found.includes(h.id)));
    scheduleRefresh();
  }, [toast, scheduleRefresh]);

  // --- Tallennin --------------------------------------------------------------
  const [recState, setRecState] = useState(null);
  const lastLoggedError = useRef(null);
  const recorder = useMemo(
    () =>
      new Recorder({
        api,
        storage,
        // Pistevarasto ja sen käynnistyshetken sisältö luetaan ennen renderöintiä (alla).
        journal: startup.journal,
        journalPoints: startup.journalPoints,
        onChange: (snap) => {
          // Lähetysvirheet lokiin vain, kun virhe vaihtuu, ettei loki täyty.
          if (snap.lastError && snap.lastError !== lastLoggedError.current) logEvent("Lähetys epäonnistui", snap.lastError, "warn");
          lastLoggedError.current = snap.lastError;
          setRecState(snap);
        },
        onAwards: (changes) => showChanges(changes),
        // Kaverit saavat retken, kun se päättyy: kerrotaan kunkin saama.
        onCompanions: (earned) =>
          earnedLines(earned, playersRef.current).forEach((line) =>
            toast({ kind: "info", what: `${line.name}: ${fmtPoints(line.points)} pistettä`, sub: line.text }),
          ),
      }),
    [],
  );
  const rec = recState ?? recorder.snapshot();

  // Muotolaskenta (neliö, klusteri) on raskaampaa kuin ruutujen haku, joten se
  // haetaan vain kun jokin sitä näyttävä paikka on auki. Retken aikana laskenta
  // tehdään enintään kerran kymmenessä sekunnissa.
  const needShapes = shapesWanted(overlays) || tab === "pass";
  const lastShapes = useRef(0);
  const shapeTimer = useRef(null);
  useEffect(() => {
    // Jo ajastettu haku riittää: se lukee suodatuksen vasta suorittaessaan, joten
    // tiheä päivitys ei jatkuvasti lykkää hakua.
    if (!needShapes || shapeTimer.current) return;
    const wait = rec.active ? Math.max(600, 10_000 - (Date.now() - lastShapes.current)) : 600;
    shapeTimer.current = setTimeout(async () => {
      shapeTimer.current = null;
      lastShapes.current = Date.now();
      const who = playerRef.current;
      try {
        const found = await api.shapes(null, modesRef.current);
        if (playerRef.current === who) setShapes(found);
      } catch {
        /* näkymä jää edelliseen tilaan; seuraava päivitys yrittää uudelleen */
      }
    }, wait);
  }, [needShapes, dataVersion, modesKey, Boolean(rec.active)]);
  useEffect(() => () => clearTimeout(shapeTimer.current), []);

  const simulator = useMemo(() => (DEV ? simulatorSource() : null), []);
  const source = useMemo(() => simulator ?? defaultLocationSource(), []);

  // Sijainnin seuranta on päällä aina, kun retki on käynnissä, myös jos
  // sovellus käynnistyi uudelleen kesken retken.
  const activeId = rec.active?.id;
  useEffect(() => {
    if (!activeId) return undefined;
    logEvent("Sijainnin seuranta käynnistyi", source.kind);
    let stop = null;
    let cancelled = false;
    source
      .watch(
        (loc) => {
          setLocationError(null);
          setMe(loc);
          recorder.addLocation(loc);
        },
        (err) => {
          setLocationError(err.message);
          logEvent("Sijaintivirhe", err.message, "warn");
        },
      )
      .then((fn) => (cancelled ? fn() : (stop = fn)));
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [activeId]);

  // Salaisten kohteiden vihjeet: kysytään palvelimelta, kun sijainti on muuttunut tarpeeksi (lib/hidden.js).
  // Palvelin vastaa vain vihjeellä ja epätarkalla renkaalla. Virheet ohitetaan hiljaa: ilman yhteyttä
  // vihjeitä ei vain tule, ja retki jatkuu normaalisti.
  const hintPoll = useRef({ last: null, busy: false, known: new Set() });
  useEffect(() => {
    const state = hintPoll.current;
    if (!me || state.busy || document.visibilityState === "hidden" || !pollDue(state.last, me, Date.now())) return;
    state.busy = true;
    const who = playerRef.current;
    state.last = { at: Date.now(), latitude: me.latitude, longitude: me.longitude };
    api
      .hiddenNearby(me.latitude, me.longitude)
      .then((r) => {
        if (playerRef.current !== who) return;
        setHints(r.hints);
        const fresh = unannounced(r.hints.map((h) => ({ ...h, id: `${who}:${h.id}` })), state.known);
        fresh.forEach((h) => toast(hintToast(h)));
        if (fresh.length) vibrate(80);
      })
      .catch(() => {})
      .finally(() => {
        state.busy = false;
      });
  }, [me?.latitude, me?.longitude]);

  // Lähetysjono: yritetään heti, verkon palatessa ja minuutin välein.
  useEffect(() => {
    logEvent("Sovellus käynnistyi", recorder.active ? "keskeneräinen retki palautettiin" : "");
    refresh();
    recorder.sync();
    flushAdventures();
    const online = () => {
      recorder.sync();
      flushAdventures();
    };
    window.addEventListener("online", online);
    const id = setInterval(online, 60_000);
    return () => {
      window.removeEventListener("online", online);
      clearInterval(id);
    };
  }, []);

  // --- Toiminnot näkymille ------------------------------------------------------
  const run = async (fn, okToast) => {
    try {
      const result = await fn();
      showChanges(result?.changes);
      if (okToast && !result?.changes?.length) toast(okToast);
      scheduleRefresh();
      return true;
    } catch (e) {
      toast({ kind: "info", what: "Toiminto ei onnistunut", sub: e.message });
      return false;
    }
  };

  // --- Seikkailut ---------------------------------------------------------------
  const outbox = useMemo(() => new AdventureOutbox({ api, storage }), []);
  const setAdventure = (value) => {
    storage.set("adventure", value);
    setAdventureState(value);
  };

  /**
   * Kirjaa seikkailun kaikille osallistujille (jokaisella on oma päiväkirja). Kirjaus tallennetaan
   * ensin jonoon ja lähetetään sitten, joten katkennut verkko ei hävitä suoritusta.
   * Palauttaa uudet merkit muodossa [{ name, badge }].
   */
  const flushAdventures = async () => {
    const sent = await outbox.flush();
    const badges = [];
    for (const { player: id, result } of sent) {
      const who = id === playerRef.current ? "" : findPlayer(playersRef.current, id)?.name ?? "Kaveri";
      for (const badge of result.new_badges ?? []) badges.push({ name: who, badge });
    }
    if (sent.length) scheduleRefresh();
    return badges;
  };
  const logAdventure = async (adv, tripId, { celebrate = false } = {}) => {
    outbox.add(completionPosts(adv, tripId));
    if (celebrate) setAdventureDone({ card: adv.card, badges: [] });
    const badges = await flushAdventures();
    // Valmistumisilmoitus näyttää merkit itse; muuten ne kerrotaan ilmoituksina.
    if (celebrate) {
      if (badges.length) setAdventureDone((d) => d && { ...d, badges });
    } else {
      badges.forEach((b) => toast({ kind: "info", what: `Uusi merkki: ${b.badge}`, sub: b.name || null }));
    }
  };

  // Leimaus: jokainen uusi sijainti tarkistetaan seikkailun rasteja vasten. Vain retken aikana,
  // koska silloin sijainti on tarkka ja ruudut kertyvät samalla. Tauolla tai siirtymässä
  // (esim. autolla) rasteja ei leimata, eikä epätarkka sijainti leimaa ohi kulkevia rasteja.
  useEffect(() => {
    const trip = rec.active;
    if (!adventure || !me || !trip || trip.pausedAt || !isEligible(trip.mode)) return;
    if (me.accuracy && me.accuracy > MAX_PUNCH_ACCURACY_M) return;
    const { reached, fresh } = punch(adventure.card.stops, adventure.reached, me.latitude, me.longitude);
    if (!fresh.length) return;
    const total = adventure.card.stops.length;
    fresh.forEach((i) => {
      const stop = adventure.card.stops[i];
      toast({ kind: "info", what: `Rasti ${i + 1} leimattu`, sub: stop.kind === "poi" ? stop.label : `${reached.length}/${total} rastia` });
    });
    vibrate(60);
    const next = { ...adventure, reached };
    if (isFinished(adventure.card, reached)) {
      setAdventure(null);
      logAdventure(next, trip.id, { celebrate: true });
    } else {
      setAdventure(next);
    }
  }, [me]);

  const player = auth.player;
  useEffect(() => { document.documentElement.style.setProperty("--player", player.color || ""); }, [player.color]);

  // Urheilusovelluksen Jaa-valikosta saapuva tiedosto avataan tuonnin esikatseluun päiväkirjassa. Natiivi muistaa
  // tiedoston: haetaan heti käynnistyttäessä ja aina, kun sovelluksen ollessa auki tulee uusi jako.
  useEffect(() => {
    if (!shareImport.supported) return undefined;
    const check = async () => {
      try {
        const file = await shareImport.take();
        if (!file) return;
        logEvent("Tiedosto jaettu sovellukseen", file.name);
        setTool(null);
        setOpenItem(null);
        setOpenTripId(null);
        setTab("diary");
        setSharedFile(file);
      } catch (e) {
        logEvent("Jaetun tiedoston vastaanotto epäonnistui", e?.code ?? e?.message ?? "", "warn");
        toast({ kind: "info", what: "Tiedoston tuonti ei onnistunut", sub: shareErrorText(e) });
      }
    };
    check();
    return shareImport.listen(check);
  }, []);

  // Edellisen viikon ja kuukauden voittaja ilmoitetaan kerran kutakin kautta kohden pelaajaa kohden.
  // Haku tehdään vain kun pelaajia on useampi, ja vain kerran per pelaaja sovelluksen käynnistyksen aikana.
  const winnersChecked = useRef(new Set());
  useEffect(() => {
    if (!player || (players?.length ?? 0) < 2 || winnersChecked.current.has(player.id)) return;
    winnersChecked.current.add(player.id);
    api.leaderboardWinners().then(
      (data) => {
        const notices = pendingWinnerNotices(data, storage.get("winnerSeen", {}), player.id);
        notices.forEach((n) => toast({ kind: "info", what: `${n.title}: ${n.mine ? "sinä!" : n.names.join(" ja ")}`, sub: n.text }));
        if (notices.length) storage.set("winnerSeen", markWinnersSeen(storage.get("winnerSeen", {}), player.id, notices));
      },
      () => winnersChecked.current.delete(player.id), // yritetään uudelleen seuraavalla päivityksellä
    );
  }, [player?.id, players?.length]);

  // The signed-in account owns exactly one player. Team membership never selects another identity.

  const switchPlayer = (id) => {
    if (id !== auth.player.id) toast({ kind: "info", what: "Vaihda käyttäjätiliä kirjautumisesta." });
  };
  // --- Androidin takaisin-painike ------------------------------------------------
  // Oletuksena painike sulkisi koko sovelluksen. Nyt se sulkee ensin avoimen ikkunan, sitten työkalun,
  // kohteen tai retken, palaa kartalle ja minimoi sovelluksen vasta kartalta (kesken oleva retki jatkuu).
  // Selaimessa ei ole natiivia painiketta, joten kuuntelija asennetaan vain puhelinsovelluksessa.
  const backState = useRef({});
  backState.current = { tab, tool: Boolean(tool), item: Boolean(openItem), trip: Boolean(openTripId) };
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return undefined;
    let handle = null;
    let gone = false;
    import("@capacitor/app")
      .then(({ App: CapacitorApp }) => {
        if (gone) return null;
        return CapacitorApp.addListener("backButton", () => {
          const st = backState.current;
          const step = backStep({ sheet: backStack.size() > 0, tool: st.tool, item: st.item, trip: st.trip, tab: st.tab });
          if (step === "sheet") backStack.closeTop();
          else if (step === "tool") setTool(null);
          else if (step === "item") setOpenItem(null);
          else if (step === "trip") setOpenTripId(null);
          else if (step === "map") setTab("map");
          else CapacitorApp.minimizeApp();
        });
      })
      .then((h) => {
        if (gone) h?.remove();
        else handle = h;
      })
      .catch(() => {}); // lisäosan puuttuminen ei saa kaataa sovellusta: silloin painike toimii kuten ennen
    return () => {
      gone = true;
      handle?.remove();
    };
  }, []);

  const update = useAppUpdate(api); // päivitysilmoitus (vain Androidin APK-sovellus)

  const app = {
    update,
    api, stats, tiles, content, trips, tripsStatus, wishlists, loadError, dataVersion, rec, me, theme, setTheme, skin, setSkin, simulator, locationError,
    toast, refresh,
    account: auth.account, membership: auth.membership, refreshIdentity: auth.refreshIdentity, logout: auth.logout,
    players: [player], publicPlayers: players, activePlayer, player, companions,
    switchPlayer,
    showWelcome: false,
    /** Pelaajat, jotka tämä puhelin saa valita (ylläpitäjällä kaikki). */
    visiblePlayers: [player],
    otherPlayers: [],
    /** Ensikäynnistys: luodaan oma pelaaja ja siirrytään heti pelaamaan hänenä. */
    setCompanions: () => setCompanionsState([]),
    savePlayer: async (id, body) => {
      if (id !== auth.player.id) return false;
      try { await api.updatePlayer(id, body); await auth.refreshIdentity(); await refresh(); return true; }
      catch (e) { toast({ kind: "info", what: "Profiilia ei tallennettu", sub: e.message }); return false; }
    },
    /** Muuttaa päättyneen retken kavereita. Uudet kaverit saavat retken, poistetut menettävät sen. */
    setTripCompanions: (tripId, ids) =>
      run(async () => {
        const r = await api.setCompanions(tripId, ids);
        earnedLines(r.companions_earned, playersRef.current).forEach((l) => toast({ kind: "info", what: `${l.name}: ${fmtPoints(l.points)} pistettä`, sub: l.text }));
        return r;
      }, { kind: "info", what: "Kaverit tallennettu" }),
    adventure, adventureDone, adventureLog,
    closeAdventureDone: () => setAdventureDone(null),
    /** Ottaa kortin käyttöön: kaverit tallentuvat retkivalintaan ja kartta tarkentuu reittiin. */
    chooseAdventure: (card, mates) => {
      if (!recorder.active) app.setCompanions(mates);
      setAdventure(startAdventure(card, { player: playerRef.current, companions: recorder.active ? recorder.active.companions ?? [] : mates }));
      app.showOnMap(routeBounds(card), { padBottom: ADVENTURE_DOCK_PX }); // reitti jää seikkailupalkin yläpuolelle
      logEvent("Seikkailu valittu", `${card.length}, ${card.stops.length} rastia`);
    },
    cancelAdventure: () => setAdventure(null),
    tool, setTool,
    hints,
    // Ylläpito: avain tarkistetaan palvelimella ennen tallennusta.
    admin: auth.account.role === "admin",
    enableAdmin: async (key) => {
      const trimmed = key.trim();
      if (!/^[\x21-\x7e]+$/.test(trimmed)) return "Avaimessa saa olla vain kirjaimia, numeroita ja merkkejä ilman välilyöntejä tai ääkkösiä.";
      try {
        await api.adminStatus(trimmed);
      } catch (e) {
        return e.status === 401 ? "Avain ei kelpaa." : e.status === 403 ? "Ylläpito ei ole käytössä palvelimella." : e.message;
      }
      storage.set("adminKey", trimmed);
      setAdminKeyState(trimmed);
      logEvent("Ylläpito otettu käyttöön");
      return null;
    },
    disableAdmin: () => {
      storage.set("adminKey", "");
      setAdminKeyState("");
      setHiddenForm(null);
      logEvent("Ylläpito poistettu käytöstä");
    },
    hiddenForm,
    openHiddenForm: (form) => setHiddenForm(form),
    closeHiddenForm: () => setHiddenForm(null),
    weekly, setWeekly,
    filteredTiles, shapes, mapModes, overlays,
    setMapModes: (modes) => {
      storage.set("mapModes", modes);
      setMapModesState(modes);
    },
    setOverlay: (key, value) => {
      setOverlaysState((current) => {
        const next = { ...current, [key]: value };
        storage.set("overlays", next);
        return next;
      });
    },
    focus,
    clearFocus: () => setFocus(null),
    mapRoute,
    mapPoi,
    clearMapRoute: () => setMapRoute(null),
    clearMapPoi: () => setMapPoi(null),
    /**
     * Kohde kartalle: siirtyy karttaan, tarkentaa kohteeseen ja näyttää sen tunnistusalueen. Kohde ilman
     * sijaintia ei näy kartalla, joten sen kortti avataan suoraan.
     */
    showPoi: (id) => {
      const bounds = poiBounds(content?.pois?.find((p) => p.id === id));
      if (!bounds) return setOpenItem({ kind: "poi", id });
      setTool(null);
      setOpenItem(null);
      setMapRoute(null); // kartalla on kerrallaan yksi valinta
      setMapPoi(id);
      setTab("map");
      setFocus({ bounds, padBottom: ROUTE_DOCK_PX });
    },
    /**
     * Reitti kartalle: siirtyy karttaan, tarkentaa reitin linjaan ja korostaa sen. Reitti ilman linjaa
     * ei näy kartalla, joten sen kortti avataan suoraan.
     */
    showRoute: (id) => {
      id = content?.route_aliases?.[id] ?? id;
      const route = content?.routes?.find((r) => r.id === id);
      const bounds = lineBounds(route?.geometry);
      if (!bounds) return setOpenItem({ kind: "route", id });
      setTool(null);
      setOpenItem(null);
      setMapPoi(null);
      setMapRoute(id);
      setTab("map");
      setFocus({ bounds, padBottom: ROUTE_DOCK_PX });
    },
    /** Siirtyy kartalle ja tarkentaa annettuihin rajoihin (esim. Passin neliökortista). */
    showOnMap: (bounds, { padBottom = 0 } = {}) => {
      setTool(null);
      setOpenItem(null);
      setTab("map");
      setFocus({ bounds, padBottom });
    },
    markImpossible: (key, note) => run(() => api.markImpossible(key, note), { kind: "info", what: "Ruutu merkitty mahdottomaksi", sub: "Se ei näy enää ehdotuksissa." }),
    unmarkImpossible: (key) => run(() => api.unmarkImpossible(key), { kind: "info", what: "Merkintä poistettu" }),
    mapLayers,
    basemapChoice,
    basemap: effectiveBasemap(basemapChoice, mapLayers),
    setBasemap: (id) => {
      storage.set("basemap", id);
      setBasemapChoiceState(id);
    },
    /** Avaa retken päiväkirjassa, esim. merkin kautta. */
    openTrip: (id) => {
      setTool(null);
      setTab("diary");
      setOpenTripId(id);
    },
    /** Siirtyy näkymään, esim. tukikohdan rakennuksesta päiväkirjaan. */
    goTo: (id) => {
      setTool(null);
      setOpenTripId(null);
      setTab(id);
    },
    startTool: (t) => {
      setOpenItem(null);
      setTab("map");
      setTool(t);
    },
    endTool: () => setTool(null),
    /** Karttatarkistuksen tallennus: kaikki retket arvioidaan palvelimella uudelleen. */
    saveContentCheck: (fn, what) => run(fn, { kind: "info", what }),
    /** Lisää tai poistaa kohteen toivelistalta. */
    toggleWish: (listId, key, present) => run(() => (present ? api.removeWish(listId, key) : api.addWish(listId, key))),
    createWishlist: async (name) => {
      try {
        const { id } = await api.createWishlist(name);
        await refresh();
        return id;
      } catch (e) {
        toast({ kind: "info", what: "Listaa ei luotu", sub: e.message });
        return null;
      }
    },
    openTripId, setOpenTripId,
    sharedFile,
    clearSharedFile: () => setSharedFile(null),
    openContent: (kind, id) => setOpenItem({ kind, id: kind === "route" ? content?.route_aliases?.[id] ?? id : id }),
    startTrip: (mode) => {
      const mates = cleanCompanions(companions, players, playerRef.current);
      recorder.start(mode, { player: playerRef.current, companions: mates });
      // Seikkailu kirjataan niille, jotka lopulta lähtivät mukaan (kaverit voi vaihtaa lähtöikkunassa).
      if (adventure) setAdventure({ ...adventure, companions: mates });
      logEvent("Retki aloitettu", modeLabel(mode));
      setLocationError(null);
      if (simulator && me) recorder.addLocation({ ...me, time: Date.now() });
    },
    changeMode: (mode) => recorder.changeMode(mode),
    pauseTrip: () => recorder.pause(),
    resumeTrip: () => recorder.resume(),
    finishTrip: async () => {
      const snap = recorder.snapshot().active;
      // Kesken jäänyt seikkailu kirjataan osittaisena (päiväkirjaan), mutta ei merkitä suoritetuksi.
      if (adventure) {
        if (adventure.reached.length) logAdventure(adventure, snap.id);
        setAdventure(null);
      }
      recorder.finish();
      await recorder.sync();
      const queued = recorder.outbox.length > 0;
      logEvent("Retki lopetettu", queued ? `odottaa lähetystä: ${recorder.pendingPointCount} pistettä` : "lähetetty");
      toast({
        kind: "info",
        what: queued ? "Retki tallessa puhelimessa" : "Retki tallennettu",
        sub: queued ? "Se lähetetään, kun yhteys palvelimeen toimii." : `${km(snap.distanceM)}, ${snap.newTiles} uutta ruutua, ${fmtPoints(snap.earned)} pistettä`,
      });
      refresh();
    },
    // Retken hylkäys: ei tallenneta. Varmistus kysytään käyttöliittymässä ennen tätä kutsua.
    discardTrip: async () => {
      // Kesken jäänyttä seikkailua ei kirjata päiväkirjaan, koska retkeäkään ei tallenneta.
      if (adventure) setAdventure(null);
      recorder.discard();
      await recorder.sync();
      const queued = recorder.discards.length > 0;
      logEvent("Retki hylätty", queued ? "poisto palvelimelta odottaa yhteyttä" : "poistettu");
      toast({
        kind: "info",
        what: "Retki hylätty",
        sub: queued ? "Poisto palvelimelta tehdään, kun yhteys toimii." : "Mitään ei tallennettu.",
      });
      refresh();
    },
    syncNow: () => recorder.sync().then(refresh),
    locateOnce: () =>
      navigator.geolocation?.getCurrentPosition(
        (p) => {
          setMe({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy });
          setCenterRequest((r) => ({ id: (r?.id ?? 0) + 1, force: true })); // painikkeen painaja haluaa nähdä itsensä
        },
        () => toast({ kind: "info", what: "Sijaintia ei saatu", sub: "Salli sijainti, niin kartta keskittyy sinuun." }),
        { enableHighAccuracy: true, timeout: 15_000 },
      ),
    centerRequest,
    confirm: (body) => run(() => api.confirm(body), { kind: "info", what: "Merkintä tallennettu", sub: "Palkinto oli jo saatu aiemmin." }),
    revoke: (id) => run(() => api.revoke(id), { kind: "info", what: "Oma vahvistus peruttu" }),
    replaceModes: (id, spans) => run(() => api.replaceModes(id, spans), { kind: "info", what: "Kulkutavat korjattu" }),
    importTracks: async (filename, b64, tracks, withCompanions = []) => {
      const ok = await run(async () => {
        const r = await api.importTracks(filename, b64, tracks, withCompanions);
        earnedLines(r.companions_earned, playersRef.current).forEach((l) => toast({ kind: "info", what: `${l.name}: ${fmtPoints(l.points)} pistettä`, sub: l.text }));
        const created = r.imported.filter((x) => x.created).length;
        logEvent("Retki tuotu tiedostosta", `${created} uutta`);
        if (!r.changes.length) toast({ kind: "info", what: created ? "Retki tuotu" : "Retki oli jo tuotu", sub: created ? "Ei uusia ruutuja tällä kertaa." : null });
        return r;
      });
      return ok;
    },
    renameTrip: (id, name) => run(() => api.renameTrip(id, name), { kind: "info", what: "Nimi tallennettu" }),
    deleteTrip: (id) => run(() => api.deleteTrip(id), { kind: "info", what: "Retki poistettu" }),
  };

  // Kartta keskitetään pelaajaan, kun sovellus avataan ja kun se tulee takaisin esiin pitkän tauon jälkeen.
  // Sijainti haetaan kertahaulla (retken seuranta antaa sen vasta retken aikana). Epäonnistuminen on
  // hiljaista: lupa voi puuttua tai paikannus olla pois, jolloin kartta jää oletusnäkymään ja
  // "Keskitä omaan sijaintiin" -painike kertoo syyn.
  useEffect(() => {
    if (simulator) return undefined; // simulaattorilla on oma aloituspiste
    const locateQuietly = (force) => {
      if (!navigator.geolocation || recorder.snapshot().active) return; // retken aikana seuranta hoitaa sijainnin
      navigator.geolocation.getCurrentPosition(
        (p) => {
          setMe({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy });
          setCenterRequest((r) => ({ id: (r?.id ?? 0) + 1, force }));
        },
        (err) => logEvent("Aloitussijaintia ei saatu", err.message || String(err.code), "info"),
        { enableHighAccuracy: true, timeout: 20_000, maximumAge: 60_000 },
      );
    };
    locateQuietly(false);
    let hiddenSince = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") hiddenSince = Date.now();
      else {
        if (needsRecenter(hiddenSince, Date.now())) locateQuietly(true);
        hiddenSince = null;
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
  // Simulaattorissa aloituspiste on Lappeenrannan satama, jos oikeaa sijaintia ei ole.
  useEffect(() => {
    if (simulator && !me) setMe({ latitude: 61.0645, longitude: 28.1905, accuracy: 5 });
  }, []);
  // Simulaattorin sijainnit päivittävät myös "minä"-merkin.
  useEffect(() => {
    if (!simulator) return undefined;
    const original = simulator.emit;
    simulator.emit = (lat, lon, t) => {
      setMe({ latitude: lat, longitude: lon, accuracy: 5 });
      original(lat, lon, t);
    };
    return () => (simulator.emit = original);
  }, []);

  const View = { map: MapScreen, content: ContentView, pass: PassView, diary: DiaryView, base: BaseView, leaderboard: LeaderboardPage }[tab];

  return (
    <div class="shell">
      <main class="stage">
        <View app={app} />
      </main>
      <nav class="tabbar" aria-label="Päänäkymät">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            class="tab"
            aria-current={tab === id ? "page" : undefined}
            onClick={() => {
              setTab(id);
              if (id === "diary") setOpenTripId(null);
              if (id !== "map") setTool(null); // työkalu päättyy, kun karttanäkymästä poistutaan
            }}
          >
            <Icon />
            {label}
          </button>
        ))}
      </nav>
      <Toasts items={toasts} />
      {app.showWelcome && <WelcomeSheet app={app} />}
      <UpdateSheet update={update} />
      {hiddenForm && app.admin && <HiddenFormSheet app={app} form={hiddenForm} onClose={() => setHiddenForm(null)} />}
      {openItem && <ContentSheet app={app} kind={openItem.kind} id={openItem.id} onClose={() => setOpenItem(null)} />}
    </div>
  );
}

// Ennen Recorderin synkronista alustusta palautetaan mahdollinen
// IndexedDB-snapshot ja luetaan pistevaraston pisteet. Vanhan localStorage-tiedon
// voi edelleen lukea. Jos pistevarastoa ei saada auki, Recorder toimii ilman sitä
// (pisteet pysyvät tilassa kuten ennen), eikä käynnistys estyy.
recoverRecorderSnapshot()
  .then((result) => {
    if (result.restored) logEvent("Retken tiedot palautettu", "IndexedDB-varmuuskopio");
    if (result.warning) logEvent("Paikallisen palautuksen varoitus", result.warning, "warn");
  })
  .catch((error) => logEvent("Paikallisen palautuksen virhe", String(error), "warn"))
  .then(async () => {
    try {
      const journal = createPointJournal();
      startup.journalPoints = await journal.readAll();
      startup.journal = journal;
      const restored = [...startup.journalPoints.values()].reduce((sum, pts) => sum + pts.length, 0);
      if (restored) logEvent("Pistevarasto luettu", `${restored} lähettämätöntä pistettä`);
    } catch (error) {
      logEvent("Pistevarasto ei käytössä", String(error?.message ?? error), "warn");
    }
  })
  .finally(() => render(<AccountGate>{(auth) => <App key={auth.account.id} auth={auth} />}</AccountGate>, document.getElementById("app")));

